import SDKConnector from '../SDKConnector';
import { ALLOWED_SERVICES, HTTP_METHODS } from '../common/types';
import log from '../Logger';
import { serviceErrorCodeHandler, uploadLogs } from '../common/Utils';
import { METHOD_START_MESSAGE, SUCCESS_MESSAGE, SUCCESS_STATUS_CODE } from '../common/constants';
import { COMMON_EVENT_KEYS, MOBIUS_EVENT_KEYS, RECORDING_EVENT_SUBTYPE, } from '../Events/types';
import { Eventing } from '../Events/impl';
import { RecordingRequestType, } from './types';
import { CALL_RECORDING_FILE, CONVERGED_RECORDINGS, DEFAULT_MAX, DEFAULT_NUMBER_OF_DAYS, DEFAULT_STATUS, FORMAT, FROM, LOCATION_ID, MAX, METADATA, METHODS, OWNER_TYPE, RECORDING_NOT_FOUND_MESSAGE, SERVICE_TYPE, SOFT_DELETE, STATUS, STORAGE_REGION, TO, TOPIC, WEBEX_USER_REQUEST_HEADER, } from './constants';
export class WxcCallRecordingConnector extends Eventing {
    sdkConnector;
    webex;
    recordingServiceUrl;
    constructor(webex, logger) {
        super();
        this.sdkConnector = SDKConnector;
        if (!this.sdkConnector.getWebex()) {
            SDKConnector.setWebex(webex);
        }
        this.webex = this.sdkConnector.getWebex();
        this.recordingServiceUrl =
            this.webex.internal.services._serviceUrls?.hydraDeveloperApi ||
                this.webex.internal.services.get(this.webex.internal.services._activeServices?.hydraDeveloperApi);
        this.registerRecordingListeners();
        log.setLogger(logger.level, CALL_RECORDING_FILE);
    }
    buildRecordingsUrl(options) {
        const params = new URLSearchParams();
        let fromDate = options?.from;
        if (!fromDate) {
            const date = new Date();
            date.setDate(date.getDate() - (options?.days ?? DEFAULT_NUMBER_OF_DAYS));
            fromDate = date.toISOString();
        }
        const toDate = options?.to ?? new Date().toISOString();
        params.append(FROM, fromDate);
        params.append(TO, toDate);
        params.append(STATUS, options?.status ?? DEFAULT_STATUS);
        params.append(MAX, `${options?.max ?? DEFAULT_MAX}`);
        const optionalParams = [
            [SERVICE_TYPE, options?.serviceType],
            [FORMAT, options?.format],
            [OWNER_TYPE, options?.ownerType],
            [STORAGE_REGION, options?.storageRegion],
            [LOCATION_ID, options?.locationId],
            [TOPIC, options?.topic],
        ];
        optionalParams.forEach(([key, value]) => {
            if (value) {
                params.append(key, value);
            }
        });
        return `${this.recordingServiceUrl}/${CONVERGED_RECORDINGS}?${params.toString()}`;
    }
    getCallRecording(request) {
        return this.dispatchGetCallRecording(request);
    }
    dispatchGetCallRecording(request) {
        switch (request.type) {
            case RecordingRequestType.LIST:
                return this.getRecordings(request.options);
            case RecordingRequestType.DETAIL:
                return this.getRecording(request.recordingId);
            case RecordingRequestType.METADATA:
                return this.getRecordingMetadata(request.recordingId);
            case RecordingRequestType.BY_CALL_SESSION:
                return this.getRecordingsByCallSessionId(request.callSessionId, request.options);
            default: {
                const exhaustiveCheck = request;
                throw new Error(`Unsupported recording request type: ${JSON.stringify(exhaustiveCheck)}`);
            }
        }
    }
    async getRecordings(options) {
        const loggerContext = {
            file: CALL_RECORDING_FILE,
            method: METHODS.GET_RECORDINGS,
        };
        log.info(`${METHOD_START_MESSAGE} with options=${JSON.stringify(options ?? {})}`, loggerContext);
        try {
            const url = this.buildRecordingsUrl(options);
            const response = await this.webex.request({
                uri: url,
                method: HTTP_METHODS.GET,
                service: ALLOWED_SERVICES.HYDRA_DEVELOPER_API,
                ...(options?.webexUserRequest ? { headers: { [WEBEX_USER_REQUEST_HEADER]: 'true' } } : {}),
            });
            log.log(`Response trackingId: ${response?.headers?.trackingid}`, loggerContext);
            const body = (response.body ?? {});
            const responseDetails = {
                statusCode: Number(response.statusCode) || SUCCESS_STATUS_CODE,
                data: {
                    recordings: body.items ?? [],
                },
                message: SUCCESS_MESSAGE,
            };
            log.log(`Successfully retrieved ${responseDetails.data.recordings?.length ?? 0} recordings`, loggerContext);
            return responseDetails;
        }
        catch (err) {
            log.error(`Failed to get recordings: ${JSON.stringify(err)}`, loggerContext);
            await uploadLogs();
            const errorInfo = err;
            return serviceErrorCodeHandler(errorInfo, loggerContext);
        }
    }
    async getRecording(recordingId) {
        const loggerContext = {
            file: CALL_RECORDING_FILE,
            method: METHODS.GET_RECORDING,
        };
        log.info(`${METHOD_START_MESSAGE} with recordingId=${recordingId}`, loggerContext);
        try {
            const url = `${this.recordingServiceUrl}/${CONVERGED_RECORDINGS}/${recordingId}`;
            const response = await this.webex.request({
                uri: url,
                method: HTTP_METHODS.GET,
                service: ALLOWED_SERVICES.HYDRA_DEVELOPER_API,
            });
            log.log(`Response trackingId: ${response?.headers?.trackingid}`, loggerContext);
            const responseDetails = {
                statusCode: Number(response.statusCode) || SUCCESS_STATUS_CODE,
                data: {
                    recording: response.body,
                },
                message: SUCCESS_MESSAGE,
            };
            log.log(`Successfully retrieved recording ${recordingId}`, loggerContext);
            return responseDetails;
        }
        catch (err) {
            log.error(`Failed to get recording: ${JSON.stringify(err)}`, loggerContext);
            await uploadLogs();
            const errorInfo = err;
            return serviceErrorCodeHandler(errorInfo, loggerContext);
        }
    }
    async getRecordingsByCallSessionId(callSessionId, options) {
        const loggerContext = {
            file: CALL_RECORDING_FILE,
            method: METHODS.GET_RECORDINGS_BY_CALL_SESSION_ID,
        };
        log.info(`${METHOD_START_MESSAGE} with callSessionId=${callSessionId}`, loggerContext);
        const listResponse = await this.getRecordings(options);
        if (listResponse.statusCode !== SUCCESS_STATUS_CODE) {
            return listResponse;
        }
        const filtered = (listResponse.data.recordings ?? []).filter((recording) => recording.serviceData?.callSessionId === callSessionId);
        if (filtered.length === 0) {
            log.info(RECORDING_NOT_FOUND_MESSAGE, loggerContext);
        }
        else {
            log.log(`Successfully retrieved ${filtered.length} recordings for call session ${callSessionId}`, loggerContext);
        }
        return {
            statusCode: SUCCESS_STATUS_CODE,
            data: {
                recordings: filtered,
            },
            message: SUCCESS_MESSAGE,
        };
    }
    async getRecordingMetadata(recordingId) {
        const loggerContext = {
            file: CALL_RECORDING_FILE,
            method: METHODS.GET_RECORDING_METADATA,
        };
        log.info(`${METHOD_START_MESSAGE} with recordingId=${recordingId}`, loggerContext);
        try {
            const url = `${this.recordingServiceUrl}/${CONVERGED_RECORDINGS}/${recordingId}/${METADATA}`;
            const response = await this.webex.request({
                uri: url,
                method: HTTP_METHODS.GET,
                service: ALLOWED_SERVICES.HYDRA_DEVELOPER_API,
            });
            log.log(`Response trackingId: ${response?.headers?.trackingid}`, loggerContext);
            const responseDetails = {
                statusCode: Number(response.statusCode) || SUCCESS_STATUS_CODE,
                data: {
                    metadata: response.body,
                },
                message: SUCCESS_MESSAGE,
            };
            log.log(`Successfully retrieved metadata for recording ${recordingId}`, loggerContext);
            return responseDetails;
        }
        catch (err) {
            log.error(`Failed to get recording metadata: ${JSON.stringify(err)}`, loggerContext);
            await uploadLogs();
            const errorInfo = err;
            return serviceErrorCodeHandler(errorInfo, loggerContext);
        }
    }
    async deleteRecording(recordingId, _options) {
        const loggerContext = {
            file: CALL_RECORDING_FILE,
            method: METHODS.DELETE_RECORDING,
        };
        log.info(`${METHOD_START_MESSAGE} with recordingId=${recordingId}`, loggerContext);
        try {
            const url = `${this.recordingServiceUrl}/${CONVERGED_RECORDINGS}/${SOFT_DELETE}`;
            const response = await this.webex.request({
                uri: url,
                method: HTTP_METHODS.POST,
                service: ALLOWED_SERVICES.HYDRA_DEVELOPER_API,
                body: { recordingIds: [recordingId] },
            });
            log.log(`Response trackingId: ${response?.headers?.trackingid}`, loggerContext);
            const responseDetails = {
                statusCode: Number(response.statusCode) || SUCCESS_STATUS_CODE,
                data: {},
                message: SUCCESS_MESSAGE,
            };
            log.log(`Successfully moved recording ${recordingId} to recycle bin`, loggerContext);
            return responseDetails;
        }
        catch (err) {
            log.error(`Failed to delete recording: ${JSON.stringify(err)}`, loggerContext);
            await uploadLogs();
            const errorInfo = err;
            return serviceErrorCodeHandler(errorInfo, loggerContext);
        }
    }
    handleRecordingCreatedEvent = async (event) => {
        if (!event?.data?.activity) {
            return;
        }
        this.emit(COMMON_EVENT_KEYS.CALL_RECORDING_CREATED, event);
    };
    handleRecordingUpdatedEvent = async (event) => {
        if (!event?.data?.activity) {
            return;
        }
        switch (event.data.eventSubType) {
            case RECORDING_EVENT_SUBTYPE.TRASH:
            case RECORDING_EVENT_SUBTYPE.PURGE:
                this.emit(COMMON_EVENT_KEYS.CALL_RECORDING_DELETED, event);
                break;
            case RECORDING_EVENT_SUBTYPE.RESTORE:
                this.emit(COMMON_EVENT_KEYS.CALL_RECORDING_CREATED, event);
                break;
            default:
                this.emit(COMMON_EVENT_KEYS.CALL_RECORDING_UPDATED, event);
        }
    };
    handleRecordingDeletedEvent = async (event) => {
        if (!event?.data?.activity) {
            return;
        }
        this.emit(COMMON_EVENT_KEYS.CALL_RECORDING_DELETED, event);
    };
    registerRecordingListeners() {
        this.sdkConnector.registerListener(MOBIUS_EVENT_KEYS.RECORDING_EVENT_CREATED, this.handleRecordingCreatedEvent);
        this.sdkConnector.registerListener(MOBIUS_EVENT_KEYS.RECORDING_EVENT_UPDATED, this.handleRecordingUpdatedEvent);
        this.sdkConnector.registerListener(MOBIUS_EVENT_KEYS.RECORDING_EVENT_DELETED, this.handleRecordingDeletedEvent);
    }
}
